/**
 * The version of the legal documents a user accepts.
 *
 * One string covers both documents: the Terms incorporate the Privacy Policy
 * by reference, so a user accepts the pair. Bumping this asks every account to
 * accept again the next time they open the console.
 *
 * `2026-09-19` switches on Google Analytics, on every surface including the
 * audience's phone, and it is the largest single reversal this policy has
 * carried. Until this version the Privacy Policy opened its cookie section
 * with "FloorPoll sets no cookies and runs no third-party analytics,
 * advertising, or tracking scripts" and named Google Analytics as a thing that
 * did not exist here; the processor list said in as many words that there was
 * no analytics vendor. All three sentences are now false, and the page has
 * been rewritten around what replaced them: a consent banner, two Google
 * cookies set only on acceptance, Google added to the processor list and to
 * the transfers section, a 14-month retention entry, consent named as the
 * legal basis, and a withdraw control on the page itself.
 *
 * A bump was never in question. This is not a fuller description of something
 * already happening — it is new collection, by a new processor, on a surface
 * that previously sent nothing, and the old text told every reader the
 * opposite. Every test the entries below apply lands on the "own version"
 * side at once.
 *
 * **Its blast radius is worth knowing before the deploy, not after.** The
 * acceptance gate covers every console route, so this puts a screen over a
 * presenter's controls on their next page load — time it away from anyone's
 * event, as the note at the foot of this comment has said since `2026-08-25`.
 * Lifecycle email is also paused per account until that account re-accepts,
 * because the stored acceptance is one of the sequence's three guards. Both
 * were raised with the product owner before the work started and accepted.
 *
 * **It carries no notice period, and unlike the two departures recorded
 * elsewhere in this codebase, it does not owe one.** §8's 30-day promise binds
 * a *new limit on a free workspace*, and analytics is not a limit — nothing a
 * free workspace could do before this version becomes unavailable or paid. The
 * consent banner is the notice, it arrives before anything is stored rather
 * than 30 days after, and declining costs the reader nothing.
 *
 * `2026-08-28` carries images in questions and options (#75). Two claims in the
 * Privacy Policy were written as closed enumerations and stop being true: "What
 * we collect" listed uploads as "any logo or background image you upload for a
 * poll's **theme**", and the retention list keyed its bullet to "Uploaded
 * **theme** images". Both now name the poll's own content images too.
 *
 * Bumped rather than shipped silently on the same test the entry below applies:
 * this is a **new kind of upload**, not a fuller description of one that was
 * already happening. Before this change an author could put an image in exactly
 * two places, both of them branding; after it, a picture can sit on any question
 * and any option, and the retention promise has to cover files that did not
 * exist when it was written. A reader checking the old text against the product
 * would have found it wrong, which is the line the README draws between a bump
 * and a typo.
 *
 * It carries no new *audience-side* collection: an image is authored content
 * that public views render, so nothing about what a taker's phone sends changes,
 * and the anonymity claims are untouched.
 *
 * **It also carries §8's vendor-mark date, moved from 1 October 2026 to this
 * version's own effective date** — a product-owner decision on 27 Aug 2026 to
 * start marking free polls at the next release rather than wait. Folded into
 * this version rather than given one of its own, which the entries below
 * explain is normally unsafe. It is safe here for the reason those entries
 * name: no version from `2026-08-24` onward has reached production, and this
 * edit lands before the release that first publishes any of them, so no
 * account can ever accept `2026-08-28` in its earlier wording. **That safety
 * expires the moment a release is published from a commit before this one** —
 * if that happens, this needs its own version rather than a fold.
 *
 * What it does not do is restore the notice §8 promises. Accepting this
 * version is how a free workspace is told the mark is on, and it is told on
 * the day it binds rather than thirty days earlier. See `VENDOR_MARK_FROM`,
 * where the departure is recorded in full.
 *
 * `2026-08-27` carries the lifecycle-email edits: a new purpose in "How we use
 * what we collect", the legitimate-interests basis that covers it, SES named
 * as carrying it, and the replacement of the flat "no customer-messaging tool"
 * sentence with what is actually true.
 *
 * It is bumped rather than shipped silently because it is **new use of
 * personal data**, not a fuller description of use that already happened. The
 * in-app guide's storage-table edit skipped a bump on exactly that reasoning —
 * it "grants no new data use" — and the reasoning does not reach here: before
 * this change the policy enumerated three kinds of email and said there was no
 * customer-messaging tool, and after it there is a fourth kind. Publishing
 * that quietly would leave every existing account subscribed to something they
 * were never shown.
 *
 * **It does not undo the notice `2026-08-26` was giving.** That bump was doing
 * double duty as the 30 days §8 owes before the vendor mark binds, and this
 * one supersedes it — an account that accepts this version never accepts that
 * one. The notice survives because it lives in the *text*, not in the version
 * string: §8's vendor-mark clause is still on the Terms page every account is
 * shown here, so accepting this is being told the same thing. Deleting that
 * clause would break the notice; landing a later version on top of it does not.
 *
 * `2026-08-26` carries the vendor mark (#91): §8 now says that from
 * `VENDOR_MARK_DATE` a free poll shows a "Powered by FloorPoll" credit on the
 * big screen and on the audience's phones, and names the three things no plan
 * removes.
 *
 * `2026-08-25` was the paid-plan edits (#90): §8 Price, Stripe as a
 * sub-processor, the billing data category, and the seven-year invoice
 * retention. It is a distinct version from `2026-08-24` — the device-counting
 * edits (#89) — rather than a rewrite of it, even though neither had reached
 * prod yet and folding them into one acceptance was tempting. Folding is only
 * safe while the release ordering holds, and a release published from any
 * commit in between would put the earlier text live under a version this text
 * then shares: every prod account would be shown paid-plan terms it had
 * already "accepted". The cost of separating them is one extra acceptance
 * prompt; the cost of being wrong about the ordering is nobody accepting the
 * terms they are charged under. Every bump since has stayed distinct for that
 * same reason, this one included.
 *
 * **Time the deploy.** The acceptance gate covers every console route,
 * including `/polls/:quizId/present`, so a bump can put an acceptance screen
 * over a presenter's controls on their next page load. That is worth timing
 * away from anyone's event.
 */
export const TERMS_VERSION = '2026-09-19';

/** The same date, as the documents print it. */
export const TERMS_EFFECTIVE_DATE = '19 September 2026';

/**
 * Whether this account still owes us an acceptance.
 *
 * Compared by equality rather than ordering: ordering would need date parsing
 * and would behave strangely if a version were ever rolled back, whereas
 * "anything that is not the current version must be accepted" is correct in
 * both directions.
 */
export function needsTermsAcceptance(user: { termsVersion?: string } | null): boolean {
  if (!user) return false;
  return user.termsVersion !== TERMS_VERSION;
}
